import { Injectable, signal, effect, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'lumio-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** The user's persisted theme preference. */
  readonly theme = signal<Theme>(this.resolveInitialTheme());

  /** When set, overrides the applied theme without touching localStorage. */
  private readonly _override = signal<Theme | null>(null);

  constructor() {
    effect(() => {
      const override = this._override();
      const t = override ?? this.theme();
      if (!this.isBrowser) return;
      document.documentElement.setAttribute('data-theme', t);
      if (!override) {
        localStorage.setItem(STORAGE_KEY, t);
      }
    });
  }

  toggle(): void {
    this.theme.update((t) => (t === 'light' ? 'dark' : 'light'));
  }

  set(theme: Theme): void {
    this.theme.set(theme);
  }

  /**
   * Temporarily forces the applied theme without persisting to localStorage.
   * Pass `null` to lift the override and restore the user's saved preference.
   */
  overrideTheme(theme: Theme | null): void {
    this._override.set(theme);
  }

  private resolveInitialTheme(): Theme {
    if (!this.isBrowser) return 'light';
    const stored = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
